<template>
  <div class="login-container">
    <!-- 背景图片 -->
    <div class="login-bg" :style="{ backgroundImage: `url(${bgImage})` }"></div>
    
    <!-- 背景遮罩 -->
    <div class="login-overlay"></div>
    
    <!-- 登录表单容器 -->
    <div class="login-content">
      <el-card class="login-card" shadow="always">
        <!-- Logo 和标题 -->
        <div class="login-header">
          <div class="logo-wrapper">
            <img :src="logoImage" alt="Logo" class="logo-image" />
          </div>
          <h2 class="login-title">欢迎登录</h2>
          <p class="login-subtitle">请使用您的工号和密码登录系统</p>
        </div>

        <!-- 登录表单 -->
        <el-form
          ref="loginFormRef"
          :model="loginForm"
          :rules="loginRules"
          class="login-form"
          @submit.prevent="handleLogin"
        >
          <!-- 工号输入 -->
          <div class="form-field">
            <label class="field-label">工号</label>
            <el-input
              v-model="loginForm.username"
              placeholder="admin"
              size="large"
              clearable
              autocomplete="username"
            >
              <template #prefix>
                <el-icon class="input-icon"><User /></el-icon>
              </template>
            </el-input>
          </div>

          <!-- 密码输入 -->
          <div class="form-field">
            <label class="field-label">密码</label>
            <el-input
              v-model="loginForm.password"
              type="password"
              placeholder="请输入密码"
              size="large"
              show-password
              clearable
              autocomplete="current-password"
              @keyup.enter="handleLogin"
            >
              <template #prefix>
                <el-icon class="input-icon"><Lock /></el-icon>
              </template>
            </el-input>
          </div>

          <!-- 记住我 -->
          <div class="login-options">
            <el-checkbox v-model="loginForm.rememberMe" class="remember-checkbox">
              记住工号
            </el-checkbox>
          </div>

          <!-- 错误提示 -->
          <el-alert
            v-if="errorMessage"
            :title="errorMessage"
            type="error"
            :closable="false"
            class="error-alert"
          />

          <!-- 登录按钮 -->
          <el-button
            type="primary"
            size="large"
            class="login-button"
            :loading="isLoading"
            @click="handleLogin"
          >
            <el-icon v-if="!isLoading" class="mr-2"><Connection /></el-icon>
            {{ isLoading ? '登录中...' : '登录' }}
          </el-button>
        </el-form>

        <!-- 底部信息 -->
        <div class="login-footer">
          <p class="footer-text">© 信息科学与工程学院 & 百知科技</p>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { User, Lock, Connection } from '@element-plus/icons-vue'
import { login } from '@/api/auth'
import logoImage from '@/assets/styles/29dea0ea3d9810a40b4de6687948cc80980f0bff.png'
import bgImage from '@/assets/styles/1472f30dd62a299881cbd11f99bce0005a6a6c89.png'

const router = useRouter()
const loginFormRef = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')

// 表单数据
const loginForm = reactive({
  username: '',
  password: '',
  rememberMe: false
})

// 表单验证规则
const loginRules = {
  username: [
    { required: true, message: '请输入工号', trigger: 'blur' },
    { min: 3, message: '工号长度不能少于3个字符', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能少于6个字符', trigger: 'blur' }
  ]
}

// 登录处理
const handleLogin = async () => {
  if (!loginFormRef.value) return

  try {
    // 验证表单
    await loginFormRef.value.validate()
    
    errorMessage.value = ''
    isLoading.value = true

    // 调用真实的后端登录API
    const result = await login({
      userName: loginForm.username,
      userPwd: loginForm.password
    })

    const code = result?.code
    const payload = result?.data || {}
    const token = payload?.token || ''
    if (code !== 1 || !token) {
      const msg = result?.msg || '登录失败，请检查工号和密码'
      throw new Error(msg)
    }
    
    // 记住工号
    if (loginForm.rememberMe) {
      localStorage.setItem('rememberedUsername', loginForm.username)
    } else {
      localStorage.removeItem('rememberedUsername')
    }

    const userInfo = {
      name: payload.name || payload.userName || loginForm.username,
      role: payload.role || payload.roleCode || 'user',
      username: loginForm.username,
      ...payload
    }
    localStorage.setItem('userInfo', JSON.stringify(userInfo))
    ElMessage.success('欢迎回来')
    
    // 跳转到首页
    router.push('/')
  } catch (error) {
    console.error('登录失败:', error)
    
    // 处理错误信息：统一显示"工号或密码错误"，避免暴露后端异常细节
    const errorMsg = '工号用户名，或者密码错误'
    
    errorMessage.value = errorMsg
    // ElMessage.error(errorMsg) // 不再弹出Toast，只显示表单内的Alert，避免重复提示
  } finally {
    isLoading.value = false
  }
}

// 页面加载时检查是否有记住的用户名
onMounted(() => {
  const remembered = localStorage.getItem('rememberedUsername')
  if (remembered) {
    loginForm.username = remembered
    loginForm.rememberMe = true
  }
})
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow: hidden;
}

/* 背景图片 */
.login-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* 背景遮罩 */
.login-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    rgba(239, 246, 255, 0.8),
    rgba(255, 255, 255, 0.9),
    rgba(249, 250, 251, 0.8)
  );
}

/* 登录内容容器 */
.login-content {
  width: 100%;
  max-width: 28rem;
  position: relative;
  z-index: 10;
}

/* 登录卡片 */
.login-card {
  border-radius: 1rem;
  padding: 2rem;
}

:deep(.login-card .el-card__body) {
  padding: 0;
}

/* Logo 和标题区域 */
.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.logo-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.logo-image {
  height: 5rem;
  width: 5rem;
  object-fit: contain;
}

.login-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.login-subtitle {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
}

/* 表单样式 */
.login-form {
  margin-top: 1.5rem;
}

.form-field {
  margin-bottom: 1.5rem;
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.5rem;
}

:deep(.el-input__wrapper) {
  background-color: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  box-shadow: none;
  transition: all 0.2s;
}

:deep(.el-input__wrapper:hover) {
  border-color: #D1D5DB;
  background-color: #F9FAFB;
}

:deep(.el-input__wrapper.is-focus) {
  background-color: #FFFFFF;
  border-color: #274BDB;
  box-shadow: 0 0 0 3px rgba(39, 75, 219, 0.1);
}

.input-icon {
  color: #9CA3AF;
  font-size: 1.125rem;
}

/* 记住我 */
.login-options {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1.5rem;
}

.remember-checkbox {
  font-size: 0.875rem;
  color: #6B7280;
}

:deep(.remember-checkbox .el-checkbox__label) {
  color: #6B7280;
  font-size: 0.875rem;
}



/* 错误提示 */
.error-alert {
  margin-bottom: 1.5rem;
  border-radius: 0.5rem;
}

:deep(.error-alert .el-alert__title) {
  font-size: 0.875rem;
}

/* 登录按钮 */
.login-button {
  width: 100%;
  height: 2.75rem;
  font-size: 1rem;
  font-weight: 500;
  background: linear-gradient(135deg, #274BDB 0%, #1E3A8A 100%);
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 10px 20px rgba(39, 75, 219, 0.25);
  transition: all 0.2s;
}

.login-button:hover {
  background: linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%);
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(39, 75, 219, 0.3);
}

.login-button:active {
  transform: translateY(0);
}

.mr-2 {
  margin-right: 0.5rem;
}

/* 底部信息 */
.login-footer {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #E5E7EB;
  text-align: center;
}

.footer-text {
  font-size: 0.75rem;
  color: #9CA3AF;
  margin: 0;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .login-card {
    padding: 1.5rem;
  }

  .logo-image {
    height: 4rem;
    width: 4rem;
  }

  .login-title {
    font-size: 1.25rem;
  }
}
</style>
